import { useTranslation } from 'react-i18next'
/**
 * Referral Bonuses Component
 * Shows bonuses user gave (from claims) and bonuses user gained (from referrals)
 */

import { useQuery } from '@tanstack/react-query'
import { Gift, ArrowUp, ArrowDown, DollarSign, Coins, Clock, CheckCircle } from 'lucide-react'
import { api } from '../../services/api'

interface BonusGiven {
  id: string
  claimType: 'USDT' | 'TAKARA'
  claimAmount: number
  referralFee: number
  finalAmount: number
  vaultName: string
  createdAt: string
}

interface BonusGained {
  id: string
  rewardType: 'USDT' | 'TAKARA'
  level: number
  claimAmount: number
  rewardPercent: number
  rewardAmount: number
  status: 'PENDING' | 'PAID'
  paidAt: string | null
  vaultName: string
  createdAt: string
}

interface ReferralBonusesData {
  bonusesGiven: BonusGiven[]
  bonusesGained: BonusGained[]
  totals: {
    given: {
      USDT: number
      TAKARA: number
    }
    gained: {
      USDT: {
        pending: number
        paid: number
      }
      TAKARA: {
        pending: number
        paid: number
      }
    }
  }
}

export default function ReferralBonuses() {
  const { t } = useTranslation(['referral', 'common'])
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-referral-bonuses'],
    queryFn: async () => {
      const response = await api.getReferralBonuses()
      return response.data as ReferralBonusesData | null
    },
    retry: false,
  })

  if (isLoading) {
    return (
      <div className="bg-background-card rounded-xl border border-green-900/20 p-6">
        <div className="animate-pulse h-48 bg-background-elevated rounded"></div>
      </div>
    )
  }

  if (error || !data) {
    return null
  }

  const { bonusesGiven, bonusesGained, totals } = data

  // Don't show if no bonuses
  if (bonusesGiven.length === 0 && bonusesGained.length === 0) {
    return null
  }

  return (
    <div className="bg-background-card rounded-xl border border-green-900/20 p-6 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gold-500/20 rounded-lg">
          <Gift className="h-5 w-5 text-gold-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Referral Bonuses</h3>
          <p className="text-xs text-gray-400">Bonuses you gave and gained from referrals</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {/* Bonuses Given */}
        <div className="bg-background-elevated rounded-lg p-4 border border-red-500/20">
          <div className="flex items-center gap-2 mb-3">
            <ArrowUp className="h-4 w-4 text-red-400" />
            <span className="text-sm font-semibold text-white">Bonuses You Gave</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">USDT</span>
              <span className="text-sm font-bold text-red-400">
                ${totals.given.USDT.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">TAKARA</span>
              <span className="text-sm font-bold text-red-400">
                {totals.given.TAKARA.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Bonuses Gained */}
        <div className="bg-background-elevated rounded-lg p-4 border border-green-500/20">
          <div className="flex items-center gap-2 mb-3">
            <ArrowDown className="h-4 w-4 text-green-400" />
            <span className="text-sm font-semibold text-white">Bonuses You Gained</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">USDT (Paid)</span>
              <span className="text-sm font-bold text-green-400">
                ${totals.gained.USDT.paid.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">USDT (Pending)</span>
              <span className="text-sm font-bold text-yellow-400">
                ${totals.gained.USDT.pending.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">TAKARA (Paid)</span>
              <span className="text-sm font-bold text-green-400">
                {totals.gained.TAKARA.paid.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">TAKARA (Pending)</span>
              <span className="text-sm font-bold text-yellow-400">
                {totals.gained.TAKARA.pending.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bonuses Given List */}
      {bonusesGiven.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <ArrowUp className="h-4 w-4 text-red-400" />
            Bonuses You Gave (from your claims)
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {bonusesGiven.slice(0, 10).map((bonus) => (
              <div
                key={bonus.id}
                className="bg-background-elevated rounded-lg p-3 border border-red-500/10"
              >
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <div className="text-xs font-medium text-white">{bonus.vaultName}</div>
                    <div className="text-xs text-gray-400">
                      {new Date(bonus.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Claim: {bonus.claimType}</div>
                    <div className="text-sm font-bold text-red-400">
                      -{bonus.claimType === 'USDT' ? '$' : ''}
                      {bonus.referralFee.toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Claimed: {bonus.claimType === 'USDT' ? '$' : ''}
                  {bonus.claimAmount.toFixed(2)} → Received:{' '}
                  {bonus.claimType === 'USDT' ? '$' : ''}
                  {bonus.finalAmount.toFixed(2)}
                </div>
              </div>
            ))}
            {bonusesGiven.length > 10 && (
              <div className="text-xs text-gray-500 text-center py-2">
                +{bonusesGiven.length - 10} more...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bonuses Gained List */}
      {bonusesGained.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <ArrowDown className="h-4 w-4 text-green-400" />
            Bonuses You Gained (from referrals)
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {bonusesGained.slice(0, 10).map((bonus) => (
              <div
                key={bonus.id}
                className="bg-background-elevated rounded-lg p-3 border border-green-500/10"
              >
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-medium text-white">{bonus.vaultName}</div>
                      <span className="text-xs px-1.5 py-0.5 bg-gold-500/20 text-gold-300 rounded">
                        L{bonus.level}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(bonus.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 mb-1">
                      {bonus.status === 'PAID' ? (
                        <CheckCircle className="h-3 w-3 text-green-400" />
                      ) : (
                        <Clock className="h-3 w-3 text-yellow-400" />
                      )}
                      <span className="text-xs text-gray-400">{bonus.status}</span>
                    </div>
                    <div className="text-sm font-bold text-green-400">
                      +{bonus.rewardType === 'USDT' ? '$' : ''}
                      {bonus.rewardAmount.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {bonus.rewardPercent}% of {bonus.rewardType === 'USDT' ? '$' : ''}
                      {bonus.claimAmount.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {bonusesGained.length > 10 && (
              <div className="text-xs text-gray-500 text-center py-2">
                +{bonusesGained.length - 10} more...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
